import { useState, useEffect, useRef } from 'react';
import { Container, Grid, Button, Checkbox, GridCol, Group } from '@mantine/core';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import ModalComponent from '../../components/modal';
import { Language } from '../../languages/languageHandler';
import { ICurrency } from '../../../libraries/currencies';
import CurrencyInput from '../../components/currency-input';
import { StatusType } from '../../../libraries/zps';
import { IUserSummary, IUserSummaryRequest, updateUserSummary } from '../../../libraries/brisa';
import DisplayField from '../../components/display-field';
import { formatCurrAmount, formatTransactionType } from '../../utils/script';
import { formatDateTimeLabel } from '../../utils/date';
import { useStatusOperDialog } from '../../context/status-oper-dialog';
import './styles.scss';

interface AddEditUserSummaryProps {
  userSummary?: IUserSummary;
  mode?: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
}

const AddEditUserSummary = ({ userSummary, mode, opened, close, onSaved }: AddEditUserSummaryProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currency = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();
  const controller = useRef(new AbortController()).current;
  const [formData, setFormData] = useState<IUserSummaryRequest>({
    eid: userSummary?.lastOperationEid ?? 0,
    entid: userSummary?.entid || 0,
    incOrSet: true,
    uid: userSummary?.uid || 0,
    value: 0
  });

  const [loading, setLoading] = useState(false);

  // Populate form
  useEffect(() => {
    if (!userSummary) return;

    setFormData({
      eid: userSummary.lastOperationEid,
      entid: userSummary.entid,
      incOrSet: true,
      uid: userSummary.uid,
      value: 0
    });
  }, [userSummary]);

  const handleChange = (field: keyof IUserSummaryRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const response = await updateUserSummary(formData, controller);

      if (response?.data.statusOper?.code === 0) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: lang.BRISA_USER_SUMMARY_EDIT_SUCCESS,
          statusOper: response.data.statusOper
        });

        onSaved?.();
        close();
      } else {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.GLOBAL_ERROR,
          statusOper: response?.data.statusOper
        });
      }
    } catch (err) {
      handleApiError(err, lang.GLOBAL_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'new' ? lang.BRISA_USER_SUMMARY_ADD : `${lang.BRISA_USER_SUMMARY_EDIT} - ${userSummary?.uid}`;

  return (
    <ModalComponent
      show={opened}
      handleClick={close}
      title={title}
      content={
        <Container className="user-summary-modal">
          <Grid>
            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField title={lang.BRISA_USERS_SUMMARIES_USER_ID} content={userSummary?.uid} />
            </GridCol>

            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField title={lang.BRISA_USERS_SUMMARIES_USER_NAME} content={userSummary?.name} />
            </GridCol>

            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField
                title={lang.BRISA_USERS_SUMMARIES_LAST_OPER}
                content={formatTransactionType(lang, userSummary?.lastOperation)}
              />
            </GridCol>

            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField title={lang.BRISA_USERS_SUMMARIES_EID_LAST_OPER} content={userSummary?.lastOperationEid} />
            </GridCol>

            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField
                title={lang.BRISA_USERS_SUMMARIES_DATE_LAST_OPER}
                content={formatDateTimeLabel(userSummary?.lastOperationDt, region)}
              />
            </GridCol>

            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField
                title={lang.BRISA_USERS_SUMMARIES_AMOUNT_LAST_OPER}
                content={formatCurrAmount(userSummary?.lastOperationValue || 0, currency[0])}
              />
            </GridCol>

            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField
                title={lang.BRISA_USERS_SUMMARIES_CURRENT_BALANCE}
                content={formatCurrAmount(userSummary?.balance || 0, currency[0])}
              />
            </GridCol>
          </Grid>

          <Grid className="editable-values">
            <GridCol span={12}>
              <Checkbox
                label={lang.BRISA_USERS_SUMMARIES_BALANCE_ZERO}
                checked={!formData.incOrSet}
                onChange={e => handleChange('incOrSet', !e.currentTarget.checked)}
              />
            </GridCol>

            <GridCol span={4}>
              <CurrencyInput
                value={formData.value}
                label={lang.BRISA_USERS_SUMMARIES_AMOUNT_ADD}
                currencySymbol={currency[0]?.currencyStr}
                onChange={value => handleChange('value', value)}
              />
            </GridCol>
          </Grid>

          <Group className="buttons">
            <Button onClick={handleSave} loading={loading}>
              {lang.GLOBAL_SAVE_BUTTON}
            </Button>
          </Group>
        </Container>
      }
    />
  );
};

export default AddEditUserSummary;
